import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, EmptyState, LoadingSkeleton, VerticalPosterCard, cn } from "@bloom/ui";
import { CATEGORIES, categoryLabel } from "@bloom/types";
import { useLocale } from "../locale/locale-context.tsx";
import { api } from "../api/client.ts";

// Public catalog projection (matches GET /api/series). Kept loose since the
// new fields land incrementally — we only render what we strictly need.
interface SeriesItem {
  _id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
  taglineTh: string | null;
  taglineEn: string | null;
  posterUrl: string;
  categories: string[];
  freeEpisodeCount: number | null;
}

type SortKey = "default" | "newest" | "popular" | "rating";

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; labelKey: string }> = [
  { key: "default", labelKey: "series_index.sort.featured" },
  { key: "newest", labelKey: "series_index.sort.newest" },
  { key: "popular", labelKey: "series_index.sort.popular" },
  { key: "rating", labelKey: "series_index.sort.top_rated" },
];

export function SeriesIndexPage() {
  const { locale, t } = useLocale();
  const [params, setParams] = useSearchParams();
  const sort = (params.get("sort") as SortKey | null) ?? "default";
  const category = params.get("category") ?? "all";

  const [items, setItems] = useState<SeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinguish three states: loading, success (with items or empty by
  // filter), and "API unreachable". On a Pages-only deploy without the
  // Hono container, `/api/*` returns SPA HTML (200 with index.html), the
  // JSON parse throws, and we end up here. Empty-by-filter is fine to
  // render "no matches"; an actual fetch error wants a different message.
  const [apiDown, setApiDown] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  // Fetch the full catalog once on sort change. Category filter is applied
  // client-side so chip switches feel instant, no extra round trip.
  useEffect(() => {
    setLoading(true);
    setApiDown(false);
    const qs = new URLSearchParams();
    if (sort !== "default") qs.set("sort", sort);
    qs.set("limit", "50");
    void (async () => {
      try {
        const data = await api.get<{ items: SeriesItem[] }>(`/api/series?${qs.toString()}`);
        setItems(data.items);
      } catch {
        // Either a 4xx/5xx OR a JSON parse fail because the SPA-fallback
        // returned HTML for /api/*. Both reduce to "the catalog can't
        // load right now"; surface a retry CTA rather than the misleading
        // "No series match these filters" empty state.
        setItems([]);
        setApiDown(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [sort, retryNonce]);

  // Build the chip rail from the categories that actually appear in the
  // returned catalog — avoids dead chips when the seed changes shape.
  // Canonical category order is owned by `packages/types/src/categories.ts`
  // so admin + customer surfaces share one list.
  const categoryChips = useMemo(() => {
    const seen = new Set<string>();
    for (const s of items) for (const c of s.categories) seen.add(c);
    const ordered = CATEGORIES.map((c) => c.slug);
    const present = ordered.filter((c) => seen.has(c));
    for (const c of seen) if (!present.includes(c)) present.push(c);
    return present;
  }, [items]);

  const filtered = useMemo(() => {
    if (category === "all") return items;
    return items.filter((s) => s.categories.includes(category));
  }, [items, category]);

  const setSort = (next: SortKey) => {
    const p = new URLSearchParams(params);
    if (next === "default") p.delete("sort");
    else p.set("sort", next);
    setParams(p, { replace: true });
  };
  const setCategory = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("category");
    else p.set("category", next);
    setParams(p, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <h1 className="font-display text-4xl leading-tight sm:text-5xl">{t("nav.all_series")}</h1>
          <p className="text-sm text-[var(--brand-muted)]">{t("series_index.subtitle")}</p>
        </div>
        <label className="inline-flex items-center gap-2 self-start text-sm font-medium text-[var(--brand-ink)] sm:self-auto">
          <span className="text-[var(--brand-muted)]">{t("series_index.sort_label")}</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="h-9 rounded-full border border-[var(--brand-border)] bg-white px-3 text-sm font-semibold text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-rose)]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="-mx-1 flex flex-wrap gap-2 overflow-x-auto px-1 pb-1">
        <CategoryChip
          active={category === "all"}
          onClick={() => setCategory("all")}
          label={t("series_index.chip.all")}
        />
        {categoryChips.map((c) => (
          <CategoryChip
            key={c}
            active={category === c}
            onClick={() => setCategory(c)}
            label={categoryLabel(c, locale)}
          />
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {Array.from({ length: 12 }, (_, i) => (
            <LoadingSkeleton key={i} className="aspect-poster w-full" />
          ))}
        </div>
      ) : apiDown ? (
        // API unreachable (Pages-only deploy, or backend just restarted).
        // Show a distinct "service warming up" state with a retry CTA + a
        // gentle nudge to the legal/about pages that DON'T need the API.
        <EmptyState
          title={locale === "th" ? "ซีรีส์โหลดไม่สำเร็จในขณะนี้" : "Couldn't load the catalog right now"}
          description={
            locale === "th"
              ? "บริการกำลังเตรียมความพร้อม ลองรีเฟรชอีกครั้งในไม่ช้า"
              : "Our backend is warming up. Try again in a moment, or browse anyway."
          }
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => setRetryNonce((n) => n + 1)}>
                {locale === "th" ? "ลองอีกครั้ง" : "Try again"}
              </Button>
              <Link
                to="/about"
                className="inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold text-[var(--brand-ink)] underline-offset-4 hover:underline"
              >
                {t("nav.about")}
              </Link>
            </div>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("series_index.empty.title")}
          description={t("series_index.empty.description")}
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              {t("series_index.clear_filters")}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {filtered.map((s) => (
            <VerticalPosterCard
              key={s._id}
              href={`/series/${s.slug}`}
              titleTh={s.titleTh}
              titleEn={s.titleEn}
              taglineTh={s.taglineTh}
              taglineEn={s.taglineEn}
              posterUrl={s.posterUrl}
              freeEpisodeCount={s.freeEpisodeCount ?? 3}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-9 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors",
        active
          ? "border-[var(--brand-ink)] bg-[var(--brand-ink)] text-[var(--brand-paper)]"
          : "border-[var(--brand-border)] bg-white text-[var(--brand-ink)] hover:bg-[var(--brand-pink-soft)]",
      )}
    >
      {label}
    </button>
  );
}
